import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
    useParams,
    useOutletContext,
    useSearchParams,
    useNavigate,
} from 'react-router-dom';
import { config } from '../utils/config';
import DashboardMap from '../components/DashboardMap';
import Map from '../components/Map';
import SlopeInfo from '../components/SlopeInfo';
import EquipmentStatusCard from '../components/EquipmentStatusCard';
import DownloadCard from '../components/DownloadCard';
// Import badge icons for each device type
import SISIcon from '@/assets/device/SIS.svg';
import RAIcon from '@/assets/device/RA.svg';
import ALCIcon from '@/assets/device/ALC.svg';
import OWIcon from '@/assets/device/OW.svg';
import TIIcon from '@/assets/device/TI.svg';
import Filter from '../components/Filter';
import { extractDeviceType } from '../utils/device';
import { getMeasurements, getMeasurementExtremes } from '../services';
import ChartSelector from '../components/DeviceCharts';
import Mark1 from '@/assets/custom-icon.png';
import { DateTime } from 'luxon';
import './Device.css';

// Background colors by device type
const deviceTypeColors = {
    SIS: {
        bgColor: '#D3E9CE',
        styleColor: '#90AE81',
        cardItemColor1: '#CADDC0',
        cardItemColor2: '#D4EFCD',
    },
    RA: {
        bgColor: '#B5E1F3',
        styleColor: '#8AACBA',
        cardItemColor1: '#D3EBF4',
        cardItemColor2: '#E8E8E8',
    },
    ALC: {
        bgColor: '#EFE8D7',
        styleColor: '#D1C299',
        cardItemColor1: '#FBEDD4',
        cardItemColor2: '#E8E8E8',
    },
    OW: {
        bgColor: '#EFDDDD',
        styleColor: '#C08282',
        cardItemColor1: '#E4D2D0',
        cardItemColor2: '#FFE6DD',
    },
    TI: {
        bgColor: '#E8E8E8',
        styleColor: '#969494',
        cardItemColor1: '#D2D3D5',
        cardItemColor2: '#E4E5E8',
    },
};

// badge icon map
const deviceTypeIcons = {
    SIS: SISIcon,
    RA: RAIcon,
    ALC: ALCIcon,
    OW: OWIcon,
    TI: TIIcon,
};

export default function Device() {
    const [measurements, setMeasurements] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [chartStats, setChartStats] = useState([]);
    const [measurementExtremes, setMeasurementExtremes] = useState({
        min: null,
        max: null,
    });
    const [useApiExtremes, setUseApiExtremes] = useState(false);
    const { slopeData, alertData } = useOutletContext();
    const { deviceName } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const lastRangeRef = useRef({ deviceId: null, rangeKey: null });

    const isSlopeDataReady = useMemo(() => {
        if (!slopeData || typeof slopeData !== 'object') return false;
        return Object.keys(slopeData).length > 0;
    }, [slopeData]);

    const { deviceId, deviceType } = useMemo(() => {
        if (!deviceName || !isSlopeDataReady) {
            return { deviceId: null, deviceType: null };
        }

        const devicePools = {
            ALC: slopeData.force,
            OW: slopeData.water_level,
            TI: slopeData.tilt,
            // Add more device types here if needed
        };

        for (const [type, pool] of Object.entries(devicePools)) {
            if (!Array.isArray(pool)) continue;

            const hit = pool.find((d) => d?.name === deviceName);
            if (hit?.id != null) {
                // Use the matching pool key as the device type
                return { deviceId: hit.id, deviceType: type };
            }
        }

        return { deviceId: null, deviceType: null };
    }, [deviceName, isSlopeDataReady, slopeData]);

    const isDeviceMetaLoading = useMemo(() => {
        if (!deviceName) return false;
        return !isSlopeDataReady || !deviceId || !deviceType;
    }, [deviceName, deviceId, deviceType, isSlopeDataReady]);

    useEffect(() => {
        if (!deviceName || !isSlopeDataReady) return;
        if (!deviceId || !deviceType) {
            navigate('/', { replace: true });
        }
    }, [deviceName, deviceId, deviceType, isSlopeDataReady, navigate]);

    const bgColor = deviceTypeColors[deviceType]?.['bgColor'] || '#FFFFFF';
    const styleColor = deviceTypeColors[deviceType]?.['styleColor'] || '#000000';
    const cardItemColor1 = deviceTypeColors[deviceType]?.['cardItemColor1'] || '#B8A673';
    const cardItemColor2 = deviceTypeColors[deviceType]?.['cardItemColor2'] || '#E8E8E8';
    const Icon = deviceTypeIcons[deviceType] || null;

    const handleSearch = useCallback(
        async (filter = {}) => {
            if (!deviceId) {
                console.warn('deviceId not found, cannot search');
                return;
            }

            const { startDate, endDate, shouldSyncUrl = false } = filter;
            const rangeKey = startDate && endDate ? `${startDate}|${endDate}` : null;
            lastRangeRef.current = { deviceId, rangeKey };

            setIsLoading(true);
            try {
                // Build query params
                const queryParams = {
                    limit: 20000,
                    offset: 0,
                };

                // Apply date range
                if (startDate && endDate) {
                    queryParams.startDate = startDate;
                    queryParams.endDate = endDate;
                }

                // Apply frequency/interval if supported
                if (filter.frequency) {
                    queryParams.frequency = filter.frequency;
                }
                if (filter.interval) {
                    queryParams.interval = filter.interval;
                }

                const [measurementsResult, extremesResult] =
                    await Promise.allSettled([
                        getMeasurements(deviceId, queryParams),
                        getMeasurementExtremes(deviceId, {
                            startDate,
                            endDate,
                        }),
                    ]);

                const data =
                    measurementsResult.status === 'fulfilled'
                        ? measurementsResult.value
                        : [];
                if (measurementsResult.status === 'rejected') {
                    console.error(
                        'getMeasurements error:',
                        measurementsResult.reason?.message || measurementsResult.reason
                    );
                }

                setMeasurements(data || []);
                setChartStats([]);
                setUseApiExtremes(false);
                setMeasurementExtremes({ min: null, max: null });

                if (
                    extremesResult.status === 'fulfilled' &&
                    extremesResult.value &&
                    typeof extremesResult.value === 'object'
                ) {
                    const extremes = extremesResult.value;
                    const hasTiKeys =
                        'max_13' in extremes ||
                        'min_13' in extremes ||
                        'max_24' in extremes ||
                        'min_24' in extremes;

                    if (hasTiKeys) {
                        setChartStats([
                            {
                                label: '1-3',
                                max: extremes.max_13 ?? null,
                                min: extremes.min_13 ?? null,
                            },
                            {
                                label: '2-4',
                                max: extremes.max_24 ?? null,
                                min: extremes.min_24 ?? null,
                            },
                        ]);
                        setUseApiExtremes(true);
                    } else {
                        setMeasurementExtremes({
                            min: extremes.min ?? null,
                            max: extremes.max ?? null,
                        });
                    }
                } else if (extremesResult.status === 'rejected') {
                    console.error(
                        'getMeasurementExtremes error:',
                        extremesResult.reason?.message || extremesResult.reason
                    );
                }

                // Sync URL params when requested
                if (shouldSyncUrl && (startDate || endDate)) {
                    const next = new URLSearchParams(searchParams.toString());
                    let changed = false;

                    if (startDate) {
                        if (next.get('start') !== startDate) {
                            next.set('start', startDate);
                            changed = true;
                        }
                    } else if (next.has('start')) {
                        next.delete('start');
                        changed = true;
                    }

                    if (endDate) {
                        if (next.get('end') !== endDate) {
                            next.set('end', endDate);
                            changed = true;
                        }
                    } else if (next.has('end')) {
                        next.delete('end');
                        changed = true;
                    }

                    if (changed) {
                        setSearchParams(next, { replace: true });
                    }
                }
            } catch (e) {
                console.error('getMeasurements error:', e.message || e);
                setMeasurements([]);
                setMeasurementExtremes({ min: null, max: null });
                setUseApiExtremes(false);
            } finally {
                setIsLoading(false);
            }
        },
        [deviceId, searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!deviceId) return;

        const urlStart = searchParams.get('start');
        const urlEnd = searchParams.get('end');

        const startValid = urlStart && DateTime.fromISO(urlStart).isValid;
        const endValid = urlEnd && DateTime.fromISO(urlEnd).isValid;

        let startDate = urlStart;
        let endDate = urlEnd;

        if (!startValid || !endValid) {
            const now = DateTime.now().setZone('Asia/Taipei');
            const end = now.plus({ hours: 1 }).startOf('hour');
            const start = end.minus({ hours: 24 });
            const fmt = (dt) => dt.toFormat("yyyy-LL-dd'T'HH:mm");
            startDate = fmt(start);
            endDate = fmt(end);
        }

        const rangeKey = startDate && endDate ? `${startDate}|${endDate}` : null;
        const lastRange = lastRangeRef.current;
        if (
            lastRange &&
            lastRange.deviceId === deviceId &&
            lastRange.rangeKey === rangeKey
        ) {
            return;
        }

        handleSearch({
            startDate,
            endDate,
            shouldSyncUrl: Boolean(startValid && endValid),
        });
    }, [deviceId, handleSearch, searchParams]);

    const derivedMaxValue = useMemo(() => {
        if (measurementExtremes?.max != null) return measurementExtremes.max;
        if (!measurements.length) return undefined;
        return Math.max(...measurements.map((m) => m.value));
    }, [measurementExtremes, measurements]);

    const derivedMinValue = useMemo(() => {
        if (measurementExtremes?.min != null) return measurementExtremes.min;
        if (!measurements.length) return undefined;
        return Math.min(...measurements.map((m) => m.value));
    }, [measurementExtremes, measurements]);

    return (
        <div className="device-container" style={{ backgroundColor: bgColor }}>
            <header className="device-header">
                {Icon && <img src={Icon} alt={deviceType} className="device-badge" />}
                <h1 className="device-title">{deviceName}</h1>
            </header>

            <section className="device-body">
                {(isLoading || isDeviceMetaLoading) && (
                    <div
                        className="device-loading-overlay"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="loading-spinner" aria-hidden="true" />
                        <p>資料載入中...</p>
                    </div>
                )}
                <div className="device-map">
                    <DashboardMap focusDeviceName={deviceName} />
                </div>
                <div className="device-filter">
                    <Filter deviceType={deviceType} onSearch={handleSearch} />
                </div>
                <div className="device-chart">
                    <ChartSelector
                        deviceType={deviceType}
                        measurements={measurements}
                        deviceName={deviceName}
                        slopeData={slopeData}
                        deviceId={deviceId}
                        onStatsChange={useApiExtremes ? undefined : setChartStats}
                    />
                </div>
                <div className="cards-container">
                    {config.appType !== 'zoo' && (
                        <div className="left-column">
                            <SlopeInfo
                                styleColor={styleColor}
                                itemColor1={cardItemColor1}
                                itemColor2={cardItemColor2}
                                deviceId={deviceId}
                            />
                        </div>
                    )}
                    <EquipmentStatusCard
                        styleColor={styleColor}
                        deviceId={deviceId}
                        alertData={alertData}
                        maxValue={derivedMaxValue}
                        intervalExtremes={chartStats}
                        minValue={derivedMinValue}
                    />
                </div>
            </section>
        </div>
    );
}
